/*
 * Host-page half of the widget. Runs in the customer's page and defines the
 * <constell-widget> custom element the customer already embeds. Instead of mounting
 * the Vue app directly (Shadow DOM only), it renders the app inside a cross-origin,
 * fully transparent <iframe> pointing at our own `frame.html`, giving the widget a
 * completely separate document + JS context. Host-page scripts therefore cannot reach
 * the widget's DOM, CSS or events - the root cause of the focus-trap / analytics-
 * interference bugs on customer sites.
 *
 * To keep the public contract identical, this shell:
 *   - forwards its attributes into the iframe (initial load + reactive changes),
 *   - re-exposes `window.CONSTELL` on the host window and forwards calls to the frame,
 *   - re-emits analytics events on the host `window` + `dataLayer`,
 *   - resizes the iframe between a launcher-sized box (closed) and full viewport (open).
 *
 * The iframe is transparent (no background on the element or the framed document, plus
 * `color-scheme: normal` so the browser paints no default canvas), so the page shows
 * through wherever the widget itself does not draw.
 */
import {
    type BridgeCommandTyping,
    type BridgeReservationTyping,
    type BridgeResizeTyping,
    type BridgeTrackTyping,
    buildMessage,
    FRAME_TO_HOST,
    HOST_TO_FRAME,
    readBridgeMessage,
} from './bridge';
import { openReservationProvider } from '@/helpers/reservation-openers';
import { constellTrack } from '@/helpers/tracking';

// Attributes mirrored into the iframe. Kept in sync with WidgetProps / AppLoader.
const FORWARDED_ATTRIBUTES = [
    'venue',
    'button-label',
    'button-label-close',
    'locale',
    'position',
    'offset-x',
    'offset-y',
    'brand-color',
    'min-guest-amount',
    'max-guest-amount',
    'guest-amount-step',
    'max-budget-amount',
    'min-budget-amount',
    'budget-step',
] as const;

// Highest usable z-index so the widget always sits above host-page content.
const TOP_LAYER_Z_INDEX = '2147483647';

const buildFrameSrc = (bundleUrl: string, attributes: Record<string, string>): string => {
    const frameUrl = new URL('../frame.html', bundleUrl);
    frameUrl.searchParams.set('b', bundleUrl);

    Object.entries(attributes)
        .forEach(([key, value]) => { frameUrl.searchParams.set(key, value) });

    return frameUrl.href;
};

class ConstellWidgetHostElement extends HTMLElement {
    static get observedAttributes(): readonly string[] {
        return FORWARDED_ATTRIBUTES;
    }

    // Bundle URL is injected once by main.ts so the shell can resolve `frame.html`
    // (and pass its own bundle to the frame) relative to the deployed asset URL.
    static bundleUrl = '';

    private iframe: HTMLIFrameElement | null = null;
    private bridgeOrigin = '';
    private readonly onMessage = (event: MessageEvent): void => { this.handleMessage(event) };

    connectedCallback(): void {
        if (this.iframe) return;

        const { bundleUrl } = ConstellWidgetHostElement;
        const src = buildFrameSrc(bundleUrl, this.collectAttributes());
        this.bridgeOrigin = new URL(bundleUrl).origin;

        const iframe = document.createElement('iframe');
        iframe.src = src;
        iframe.title = 'Constell';
        iframe.setAttribute('allow', 'clipboard-write');
        iframe.setAttribute('allowtransparency', 'true');
        iframe.style.cssText = [
            'position: fixed',
            'border: 0',
            'margin: 0',
            'padding: 0',
            'background: transparent',
            'color-scheme: normal',
            `z-index: ${TOP_LAYER_Z_INDEX}`,

            // Start with no footprint until the frame reports its launcher size, so we
            // never flash a wrongly-sized box over the page.
            'width: 0',
            'height: 0',
            'inset: auto 0 0 auto',
        ].join(';');

        this.iframe = iframe;
        this.appendChild(iframe);

        window.addEventListener('message', this.onMessage);
        this.registerGlobalApi();
    }

    disconnectedCallback(): void {
        window.removeEventListener('message', this.onMessage);
        this.iframe?.remove();
        this.iframe = null;
    }

    attributeChangedCallback(name: string, _old: string | null, value: string | null): void {
        // Forward reactive attribute changes (e.g. locale) into the running frame.
        this.postToFrame(HOST_TO_FRAME.CONFIG, {
            name,
            value: value ?? '',
        });
    }

    private collectAttributes(): Record<string, string> {
        const attributes: Record<string, string> = {};

        FORWARDED_ATTRIBUTES.forEach(name => {
            const value = this.getAttribute(name);
            if (value !== null) attributes[name] = value;
        });

        return attributes;
    }

    private postToFrame(type: string, payload?: unknown): void {
        const target = this.iframe?.contentWindow;
        if (!target || !this.bridgeOrigin) return;

        target.postMessage(buildMessage(type, payload), this.bridgeOrigin);
    }

    private handleMessage(event: MessageEvent): void {
        if (event.source !== this.iframe?.contentWindow) return;

        const message = readBridgeMessage(event, this.bridgeOrigin);
        if (!message) return;

        switch (message.type) {
            case FRAME_TO_HOST.RESIZE:
                this.applyGeometry(message.payload as BridgeResizeTyping);
                break;
            case FRAME_TO_HOST.TRACK: {
                const { name, detail } = message.payload as BridgeTrackTyping;

                // Re-emit on the HOST window + dataLayer so customer listeners / GTM
                // still receive events exactly as before the iframe move.
                constellTrack(name, detail);
                break;
            }
            case FRAME_TO_HOST.OPEN_RESERVATION: {
                // Run the 3rd-party opener here, where the provider SDKs (FT/Gifty/_gstpln,
                // Zenchef/Mews DOM triggers) actually live - the frame can't reach them.
                const { partnerId } = message.payload as BridgeReservationTyping;
                openReservationProvider(partnerId);
                break;
            }
            default:
                break;
        }
    }

    private applyGeometry(geometry: BridgeResizeTyping): void {
        if (!this.iframe) return;

        const { open, width, height } = geometry;

        if (open) {
            // Full viewport: the widget's own (transparent) overlay/window position inside.
            this.iframe.style.inset = '0';
            this.iframe.style.width = '100vw';
            this.iframe.style.height = '100dvh';
            return;
        }

        // Closed: shrink to the launcher so the host page stays usable. The frame reports
        // the measured button box; anchoring follows the `position` attribute.
        this.iframe.style.width = `${Math.ceil(width)}px`;
        this.iframe.style.height = `${Math.ceil(height)}px`;
        this.iframe.style.inset = this.resolveClosedInset();
    }

    private resolveClosedInset(): string {
        const position = this.getAttribute('position') ?? 'bottom-right';

        if (position.includes('top')) {
            if (position.includes('left')) return '0 auto auto 0';
            if (position.includes('center')) return '0 auto auto 50%';
            return '0 0 auto auto';
        }

        if (position.includes('left')) return 'auto auto 0 0';
        if (position.includes('center')) return 'auto auto 0 50%';

        return 'auto 0 0 auto';
    }

    private registerGlobalApi(): void {
        const post = (payload: BridgeCommandTyping): void => { this.postToFrame(HOST_TO_FRAME.COMMAND, payload) };

        (window as unknown as Record<string, unknown>).CONSTELL = {
            open: () => { post({ command: 'open' }) },
            close: () => { post({ command: 'close' }) },
            toggle: () => { post({ command: 'toggle' }) },
            navigate: (target: string, payload?: Record<string, unknown>) => {
                post({
                    command: 'navigate',
                    target,
                    payload,
                });
            },
        };
    }
}

export const defineHostShell = (bundleUrl: string): void => {
    ConstellWidgetHostElement.bundleUrl = bundleUrl;

    if (!customElements.get('constell-widget')) {
        customElements.define('constell-widget', ConstellWidgetHostElement);
    }
};
